import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Box,
  Breadcrumbs,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  Link as MUILink,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { AttachFile, Home, OpenInNew, Search, Undo, ArrowForward } from '@mui/icons-material';
import dayjs from 'dayjs';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useTransactionLog, useTransactionLogDetail } from '@/hooks/useTransactionLog';
import { useCompleteCorrection } from '@/hooks/mutations/corrections/useCompleteCorrection';
import { openDocumentWithAuth, preOpenWindow } from '@/utils/pdfHelpers';
import type { CorrectionType } from '@/services/CorrectionService';
import type { TransactionAsset, TransactionLogEntry } from '@/components/transactions/mockTransactionLog';
import { TransactionTypeChip } from '@/components/transactions/TransactionTypeChip';
import { ViewerGuardedButton } from '@/components/shared/ViewerGuardedButton';
import { useTableSort } from '@/hooks/useTableSort';
import { usePersonLabels } from '@/hooks/usePersonLabels';
import { SortableTableCell } from '@/components/shared/SortableTableCell';

const formatDateTime = (iso: string): string => {
  const d = dayjs(iso);
  return d.isValid() ? d.format('DD MMM YYYY HH:mm') : iso;
};

export const CorrectionPage: React.FC = () => {
  usePageTitle('Document Correction');
  const navigate = useNavigate();
  const correctionMutation = useCompleteCorrection();
  const person = usePersonLabels('front_window');

  // Form state
  const [correctionType, setCorrectionType] = useState<CorrectionType | ''>('');
  const [selectedTransactionEntry, setSelectedTransactionEntry] = useState<TransactionLogEntry | null>(null);
  const [originalDocumentNumber, setOriginalDocumentNumber] = useState('');
  const [notes, setNotes] = useState('');

  // Transaction search state
  const [searchInput, setSearchInput] = useState('');

  // Fetch the full detail for the selected transaction (lazy-loaded)
  const { data: selectedTransaction = null } = useTransactionLogDetail(
    selectedTransactionEntry?.id ?? null,
  );

  // Item-level selection state — tracks per-asset correction quantities so
  // single rows with transQty > 1 can be partially corrected.
  const [selectedItemQuantities, setSelectedItemQuantities] = useState<Record<string, number>>({});

  // Derived flat ID list (backward-compatible with API contract)
  const selectedItemIds = useMemo(
    () => Object.entries(selectedItemQuantities).filter(([, qty]) => qty > 0).map(([id]) => id),
    [selectedItemQuantities],
  );

  const totalSelectedQty = useMemo(
    () => Object.values(selectedItemQuantities).reduce((sum, qty) => sum + qty, 0),
    [selectedItemQuantities],
  );

  // Confirmation dialog
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Fetch recent transactions for the autocomplete
  const { data: transactions, isLoading: transactionsLoading } = useTransactionLog({
    limit: 100,
    enabled: true,
  });

  // Map correction types to the document types they can correct
  const allowedDocTypes = useMemo(() => {
    if (correctionType === 'FULL_DOCUMENT') return ['3161', '1348'];
    if (correctionType === 'ITEM_LEVEL') return ['200', '7923'];
    return [];
  }, [correctionType]);

  // Build a set of transaction IDs that have already been corrected
  const correctedTransactionIds = useMemo(() => {
    if (!transactions) return new Set<string>();
    return new Set(
      transactions
        .filter((tx) => tx.originalTransactionId)
        .map((tx) => tx.originalTransactionId!),
    );
  }, [transactions]);

  // Filter transactions based on search input AND document type for selected correction type
  const filteredTransactions = useMemo(() => {
    if (!transactions) return [];
    let result = transactions;
    // Exclude correction transactions themselves and already-corrected transactions
    result = result.filter(
      (tx) =>
        !tx.transactionType.startsWith('CORRECTION_') &&
        !correctedTransactionIds.has(tx.id),
    );
    // Filter by document type matching the correction type
    if (allowedDocTypes.length > 0) {
      result = result.filter((tx) =>
        allowedDocTypes.some((dt) => tx.documentType?.includes(dt)),
      );
    }
    if (searchInput.trim()) {
      const q = searchInput.toLowerCase();
      result = result.filter(
        (tx) =>
          tx.documentNumber?.toLowerCase().includes(q) ||
          tx.memberName?.toLowerCase().includes(q) ||
          tx.performedByName?.toLowerCase().includes(q) ||
          tx.transactionType?.toLowerCase().includes(q) ||
          tx.id?.toLowerCase().includes(q),
      );
    }
    return result;
  }, [transactions, searchInput, allowedDocTypes, correctedTransactionIds]);

  const isFullDocument = correctionType === 'FULL_DOCUMENT';
  const isItemLevel = correctionType === 'ITEM_LEVEL';

  const canSubmit =
    correctionType !== '' &&
    selectedTransactionEntry !== null &&
    originalDocumentNumber.trim().length > 0 &&
    (!isItemLevel || selectedItemIds.length > 0);

  // When a transaction is selected, auto-populate fields
  const handleTransactionSelect = useCallback(
    (_e: React.SyntheticEvent, value: TransactionLogEntry | null) => {
      setSelectedTransactionEntry(value);
      if (value) {
        setOriginalDocumentNumber(value.documentNumber ?? '');
        // Reset item selections when a new transaction is chosen
        setSelectedItemQuantities({});
      } else {
        setOriginalDocumentNumber('');
        setSelectedItemQuantities({});
      }
    },
    [],
  );

  const handleToggleAllItems = useCallback(() => {
    if (!selectedTransaction?.assets) return;
    const allFullySelected = selectedTransaction.assets.every(
      (a) => (selectedItemQuantities[a.assetId] ?? 0) >= (a.transQty || 1),
    );
    if (allFullySelected) {
      setSelectedItemQuantities({});
    } else {
      const full: Record<string, number> = {};
      for (const a of selectedTransaction.assets) {
        full[a.assetId] = a.transQty || 1;
      }
      setSelectedItemQuantities(full);
    }
  }, [selectedTransaction, selectedItemQuantities]);

  const handleGroupQuantityChange = useCallback(
    (groupAssets: TransactionAsset[], desiredQty: number) => {
      setSelectedItemQuantities((prev) => {
        const next = { ...prev };
        // Clear existing selections for this group
        for (const asset of groupAssets) {
          delete next[asset.assetId];
        }
        // Distribute desiredQty across assets, filling each up to its transQty
        let remaining = desiredQty;
        for (const asset of groupAssets) {
          if (remaining <= 0) break;
          const qty = Math.min(remaining, asset.transQty || 1);
          next[asset.assetId] = qty;
          remaining -= qty;
        }
        return next;
      });
    },
    [],
  );

  const handleSubmit = useCallback(() => {
    setConfirmOpen(true);
  }, []);

  const handleConfirmCorrection = useCallback(() => {
    setConfirmOpen(false);
    if (!selectedTransactionEntry) return;

    correctionMutation.mutate(
      {
        correctionType: correctionType as CorrectionType,
        originalTransactionId: selectedTransactionEntry.id,
        originalDocumentNumber: originalDocumentNumber.trim(),
        selectedItemIds: isItemLevel ? selectedItemIds : undefined,
        selectedItemQuantities: isItemLevel ? selectedItemQuantities : undefined,
        notes: notes.trim() || undefined,
      },
      {
        onSuccess: () => {
          setCorrectionType('');
          setSelectedTransactionEntry(null);
          setSearchInput('');
          setOriginalDocumentNumber('');
          setNotes('');
          setSelectedItemQuantities({});
        },
      },
    );
  }, [
    correctionMutation,
    correctionType,
    selectedTransactionEntry,
    originalDocumentNumber,
    selectedItemIds,
    selectedItemQuantities,
    isItemLevel,
    notes,
  ]);

  const assetItems = selectedTransaction?.assets ?? [];
  const { sortField: assetSortField, sortDirection: assetSortDir, onSort: onAssetSort, sortedRows: sortedAssetItems } = useTableSort(
    assetItems,
  );

  // Group assets by LIN/NSN/nomenclature so identical items show as a single row
  // with a quantity selector instead of redundant individual checkboxes.
  const groupedAssets = useMemo(() => {
    const groups = new Map<string, { lin: string; nsn: string; nomenclature: string; assets: TransactionAsset[]; effectiveQty: number }>();
    for (const asset of assetItems) {
      const key = [asset.lin, asset.nsn, asset.nomenclature].join('|');
      const existing = groups.get(key);
      if (existing) {
        existing.assets.push(asset);
        existing.effectiveQty += (asset.transQty || 1);
      } else {
        groups.set(key, { lin: asset.lin, nsn: asset.nsn, nomenclature: asset.nomenclature, assets: [asset], effectiveQty: asset.transQty || 1 });
      }
    }
    return Array.from(groups.values());
  }, [assetItems]);

  const totalAvailableQty = useMemo(
    () => assetItems.reduce((sum, a) => sum + (a.transQty || 1), 0),
    [assetItems],
  );

  return (
    <Container maxWidth={false} sx={{ mt: 3, mb: 4 }}>
      <Breadcrumbs sx={{ mb: 2 }}>
        <MUILink
          underline="hover"
          color="inherit"
          sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
          onClick={() => navigate('/')}
        >
          <Home sx={{ mr: 0.5 }} fontSize="inherit" />
          Home
        </MUILink>
        <MUILink
          underline="hover"
          color="inherit"
          sx={{ cursor: 'pointer' }}
          onClick={() => navigate('/transactions')}
        >
          Transactions
        </MUILink>
        <Typography color="text.primary">Document Correction</Typography>
      </Breadcrumbs>

      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
        <Undo color="primary" />
        <Typography variant="h5" fontWeight={700}>
          Document Correction
        </Typography>
      </Stack>

      {/* Success alert */}
      {correctionMutation.isSuccess && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Correction completed. Document{' '}
          <strong>{correctionMutation.data.correctedDocumentNumber}</strong> created from{' '}
          {correctionMutation.data.originalDocumentNumber}.{' '}
          {correctionMutation.data.updatedAssetIds.length} asset(s) updated.
        </Alert>
      )}

      {/* Error alert */}
      {correctionMutation.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {correctionMutation.error.message}
        </Alert>
      )}

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
        {/* Left panel: correction form */}
        <Paper sx={{ p: 3, flex: 1, minWidth: { xs: 0, md: 300 } }}>
          <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
            Correction Details
          </Typography>
          <Stack spacing={3}>
            {/* Step 1: Correction Type */}
            <FormControl fullWidth>
              <InputLabel id="correction-type-label">Correction Type</InputLabel>
              <Select
                labelId="correction-type-label"
                value={correctionType}
                label="Correction Type"
                onChange={(e) => {
                  setCorrectionType(e.target.value as CorrectionType | '');
                  setSelectedTransactionEntry(null);
                  setOriginalDocumentNumber('');
                  setSearchInput('');
                  setSelectedItemQuantities({});
                }}
              >
                <MenuItem value="FULL_DOCUMENT">
                  Full Document Correction (3161, 1348)
                </MenuItem>
                <MenuItem value="ITEM_LEVEL">
                  Item-Level Correction (DD200, 7923)
                </MenuItem>
              </Select>
            </FormControl>

            {/* Steps 2+ only visible after correction type is selected */}
            {correctionType !== '' && (
            <>
            {/* Step 2: Transaction Search */}
            <Autocomplete<TransactionLogEntry, false, false, false>
              options={filteredTransactions}
              loading={transactionsLoading}
              value={selectedTransactionEntry}
              onChange={handleTransactionSelect}
              inputValue={searchInput}
              onInputChange={(_e, value) => setSearchInput(value)}
              getOptionLabel={(option) => {
                const parts = [option.documentNumber, option.memberName].filter(Boolean);
                return parts.join(' — ');
              }}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              noOptionsText="No matching transactions"
              filterOptions={(x) => x}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ width: '100%', py: 0.5 }}>
                    <TransactionTypeChip value={option.transactionType} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={600} noWrap>
                        {option.documentNumber || '—'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {option.memberName}{option.memberName && option.performedByName ? ' • ' : ''}
                        {option.performedByName ? `by ${option.performedByName}` : ''}
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                      {formatDateTime(option.dateProcessed)}
                    </Typography>
                    <Chip
                      size="small"
                      label={`${option.totalAssets} item${option.totalAssets !== 1 ? 's' : ''}`}
                      variant="outlined"
                      sx={{ flexShrink: 0 }}
                    />
                  </Stack>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search Transaction"
                  placeholder="Search by document #, name, or type..."
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <Search color="action" sx={{ mr: 0.5 }} />
                        {params.InputProps.startAdornment}
                      </>
                    ),
                    endAdornment: (
                      <>
                        {transactionsLoading && <CircularProgress color="inherit" size={16} />}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />

            {/* Step 3: Original Document Number (auto-populated, read-only) */}
            <TextField
              fullWidth
              label="Original Document Number"
              value={originalDocumentNumber}
              InputProps={{ readOnly: true }}
              helperText={
                isFullDocument
                  ? 'Auto-populated from selected transaction. Will receive an "R" suffix (e.g. 6042-4001R).'
                  : 'Auto-populated from selected transaction.'
              }
            />

            {/* Item selection (item-level only) — grouped by LIN/NSN/nomenclature */}
            {isItemLevel && assetItems.length > 0 && (
              <Box>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                  <Typography variant="subtitle2">
                    Select Items to Correct ({totalSelectedQty} of {totalAvailableQty})
                  </Typography>
                  <Button size="small" onClick={handleToggleAllItems}>
                    {totalSelectedQty === totalAvailableQty ? 'Deselect All' : 'Select All'}
                  </Button>
                </Stack>
                <List dense sx={{ maxHeight: 300, overflow: 'auto', border: 1, borderColor: 'divider', borderRadius: 1 }}>
                  {groupedAssets.map((group) => {
                    const selectedInGroup = group.assets.reduce(
                      (sum, a) => sum + (selectedItemQuantities[a.assetId] ?? 0), 0,
                    );
                    const isGroupSelected = selectedInGroup > 0;
                    const isSingle = group.effectiveQty === 1;

                    return (
                      <ListItem key={`${group.lin}|${group.nsn}|${group.nomenclature}`} disablePadding sx={{ px: 1 }}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <Checkbox
                            edge="start"
                            checked={isGroupSelected}
                            indeterminate={!isSingle && isGroupSelected && selectedInGroup < group.effectiveQty}
                            onChange={() => {
                              if (isGroupSelected) {
                                handleGroupQuantityChange(group.assets, 0);
                              } else {
                                handleGroupQuantityChange(group.assets, group.effectiveQty);
                              }
                            }}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={group.nomenclature || group.lin || 'Unknown'}
                          secondary={
                            [group.lin && `LIN: ${group.lin}`, group.nsn && `NSN: ${group.nsn}`]
                              .filter(Boolean)
                              .join(' | ') || undefined
                          }
                        />
                        {!isSingle && (
                          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flexShrink: 0 }}>
                            <TextField
                              type="number"
                              size="small"
                              value={selectedInGroup}
                              onChange={(e) => {
                                const val = Math.max(0, Math.min(group.effectiveQty, Number(e.target.value) || 0));
                                handleGroupQuantityChange(group.assets, val);
                              }}
                              inputProps={{ min: 0, max: group.effectiveQty, style: { width: 40, textAlign: 'center', padding: '4px 0' } }}
                              sx={{ width: 64 }}
                            />
                            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                              of {group.effectiveQty}
                            </Typography>
                          </Stack>
                        )}
                      </ListItem>
                    );
                  })}
                </List>
                {groupedAssets.some((g) => g.effectiveQty > 1) && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    Identical items are grouped. Adjust the quantity to perform a partial correction.
                  </Typography>
                )}
              </Box>
            )}

            {isItemLevel && assetItems.length === 0 && selectedTransaction && (
              <TextField
                fullWidth
                label="Asset IDs to Correct"
                placeholder="Comma-separated asset UUIDs"
                value={selectedItemIds.join(', ')}
                onChange={(e) => {
                  const ids = e.target.value
                    .split(',')
                    .map((s) => s.trim())
                    .filter((s) => s.length > 0);
                  const newQtys: Record<string, number> = {};
                  for (const id of ids) { newQtys[id] = 1; }
                  setSelectedItemQuantities(newQtys);
                }}
                helperText={`${selectedItemIds.length} item(s) entered for correction.`}
              />
            )}

            {/* Notes */}
            <TextField
              fullWidth
              label="Notes"
              placeholder="Reason for correction (optional)"
              multiline
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />

            {/* Submit */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, flexWrap: 'wrap' }}>
              <Button variant="outlined" onClick={() => navigate('/transactions')}>
                Cancel
              </Button>
              <ViewerGuardedButton
                variant="contained"
                color="primary"
                disabled={!canSubmit || correctionMutation.isPending}
                onClick={handleSubmit}
                startIcon={
                  correctionMutation.isPending ? <CircularProgress size={16} /> : undefined
                }
              >
                {correctionMutation.isPending ? 'Processing...' : 'Submit Correction'}
              </ViewerGuardedButton>
            </Box>
            </>
            )}
          </Stack>
        </Paper>

        {/* Right panel: selected transaction summary */}
        <Paper sx={{ p: 3, width: '100%', maxWidth: { md: 760 } }}>
          <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
            Selected Transaction
          </Typography>
          {selectedTransaction ? (
            <Stack spacing={2}>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Transaction Type
                  </Typography>
                  <Box sx={{ mt: 0.5 }}>
                    <TransactionTypeChip value={selectedTransaction.transactionType} />
                  </Box>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Document Number
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {selectedTransaction.documentNumber || '—'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Member
                  </Typography>
                  <Typography variant="body2">{selectedTransaction.memberName || '—'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Performed By
                  </Typography>
                  <Typography variant="body2">
                    {selectedTransaction.performedByName || '—'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Date Processed
                  </Typography>
                  <Typography variant="body2">
                    {formatDateTime(selectedTransaction.dateProcessed)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Transaction ID
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ wordBreak: 'break-all', fontFamily: 'monospace' }}
                  >
                    {selectedTransaction.id}
                  </Typography>
                </Box>
              </Box>

              {/* Documents */}
              {selectedTransaction.documents && selectedTransaction.documents.length > 0 && (
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Documents ({selectedTransaction.documents.length})
                  </Typography>
                  <List dense disablePadding sx={{ mt: 0.5 }}>
                    {selectedTransaction.documents.map((doc) => (
                      <ListItem key={doc.id} disablePadding sx={{ py: 0.25 }}>
                        <ListItemIcon sx={{ minWidth: 28 }}>
                          <AttachFile fontSize="small" color="action" />
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <MUILink
                              component="button"
                              underline="hover"
                              sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, border: 'none', background: 'none', cursor: 'pointer', p: 0, textAlign: 'left' }}
                              onClick={() => {
                                const docWindow = preOpenWindow();
                                void openDocumentWithAuth(doc.url, docWindow);
                              }}
                            >
                              {doc.filename}
                              <OpenInNew sx={{ fontSize: 14 }} />
                            </MUILink>
                          }
                          secondary={
                            [doc.documentNumber, doc.formType].filter(Boolean).join(' — ') || undefined
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}

              {/* Correction preview */}
              {correctionType && (
                <Alert severity="info" icon={false} sx={{ mt: 1 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Correction Preview
                  </Typography>
                  {isFullDocument && (
                    <Typography variant="body2">
                      All {selectedTransaction.totalAssets} item(s) on document{' '}
                      <strong>{originalDocumentNumber || '—'}</strong> will be corrected and
                      returned to stock. Document number →{' '}
                      <strong>{originalDocumentNumber ? `${originalDocumentNumber}R` : '—'}</strong>
                    </Typography>
                  )}
                  {isItemLevel && (
                    <Typography variant="body2">
                      <strong>{totalSelectedQty}</strong> of{' '}
                      {totalAvailableQty} item(s) will be corrected and returned to
                      the {person.lower} hand receipt.
                    </Typography>
                  )}
                </Alert>
              )}

              {/* Assets */}
              {assetItems.length > 0 && (
                <Box>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                    Assets {correctionType && isItemLevel && totalSelectedQty > 0
                      ? `(${totalSelectedQty} of ${totalAvailableQty} selected for correction)`
                      : `(${totalAvailableQty} total)`}
                  </Typography>
                  <TableContainer sx={{ maxHeight: 320 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <SortableTableCell field="nomenclature" sortField={assetSortField} sortDirection={assetSortDir} onSort={onAssetSort} sx={{ fontWeight: 700 }}>Item</SortableTableCell>
                          <SortableTableCell field="lin" sortField={assetSortField} sortDirection={assetSortDir} onSort={onAssetSort} sx={{ fontWeight: 700 }}>LIN</SortableTableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Current Status</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>After Correction</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>Owner Bal</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>Receiver Bal</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(correctionType && isItemLevel && selectedItemIds.length > 0 ? sortedAssetItems.filter((a) => selectedItemIds.includes(a.assetId)) : sortedAssetItems).map((asset) => (
                          <TableRow key={asset.assetId}>
                            <TableCell>
                              <Typography variant="body2" fontWeight={600} noWrap sx={{ maxWidth: 180 }}>
                                {asset.nomenclature}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                                {asset.lin}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                label={String(asset.receiverCloseBal !== 0 && asset.receiverCloseBal !== '0' && asset.receiverCloseBal !== 'Planned' ? 'Assigned' : 'Available')}
                                color={asset.receiverCloseBal !== 0 && asset.receiverCloseBal !== '0' && asset.receiverCloseBal !== 'Planned' ? 'warning' : 'success'}
                                variant="outlined"
                                sx={{ fontWeight: 700 }}
                              />
                            </TableCell>
                            <TableCell>
                              <Stack direction="row" alignItems="center" spacing={0.5}>
                                <ArrowForward sx={{ fontSize: 14, color: 'text.secondary' }} />
                                <Chip
                                  size="small"
                                  label={isFullDocument ? 'Available' : 'Assigned'}
                                  color={isFullDocument ? 'success' : 'warning'}
                                  variant="filled"
                                  sx={{ fontWeight: 700 }}
                                />
                              </Stack>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="caption" color="text.secondary">
                                {asset.ownerCloseBal === 'Planned' ? '—' : asset.ownerCloseBal}
                              </Typography>
                              <ArrowForward sx={{ fontSize: 12, mx: 0.5, color: 'text.disabled', verticalAlign: 'middle' }} />
                              <Typography variant="caption" fontWeight={700}>
                                {asset.ownerBegBal === 'Planned' ? '—' : asset.ownerBegBal}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="caption" color="text.secondary">
                                {asset.receiverCloseBal === 'Planned' ? '—' : asset.receiverCloseBal}
                              </Typography>
                              <ArrowForward sx={{ fontSize: 12, mx: 0.5, color: 'text.disabled', verticalAlign: 'middle' }} />
                              <Typography variant="caption" fontWeight={700}>
                                {asset.receiverBegBal === 'Planned' ? '—' : asset.receiverBegBal}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
            </Stack>
          ) : (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                py: 6,
                color: 'text.secondary',
              }}
            >
              <Search sx={{ fontSize: 48, mb: 1, opacity: 0.3 }} />
              <Typography variant="body2" color="text.secondary">
                {correctionType === ''
                  ? 'Select a correction type to begin.'
                  : 'Search and select a transaction to see its details here.'}
              </Typography>
            </Box>
          )}
        </Paper>
      </Stack>

      {/* Confirmation Dialog */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Confirm Correction</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {isFullDocument
              ? `This will correct all items on document ${originalDocumentNumber} and return them to stock. A new document with an "R" suffix will be created.`
              : `This will correct ${selectedItemIds.length} selected item(s) on document ${originalDocumentNumber} and return them to the ${person.lower} hand receipt.`}
          </DialogContentText>
          <DialogContentText sx={{ mt: 1, fontWeight: 700 }}>
            This action cannot be undone. Continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button variant="contained" color="primary" onClick={handleConfirmCorrection}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};
